import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, LogOut, RefreshCw, ArrowLeft, MessageCircle, ChevronRight, ChevronDown } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import { supabase } from "@/integrations/supabase/client";
import { listLeads, deleteLead, checkIsAdmin } from "@/lib/admin.functions";
import { getSessionsByPath } from "@/lib/leads/funnel.functions";
import { listPurchasesByEmail, type PurchaseTag } from "@/lib/leads/purchases.functions";
import { listAttributedPurchases } from "@/lib/leads/intel.functions";
import { SELLERS } from "@/lib/leads/sellers";
import { BLACK_SELLERS } from "@/lib/black2x1/sellers";
import { formatBR } from "@/lib/datetime";
import { toE164BR } from "@/lib/leads/phone";
import { RDCRM_STUDENT_STAGE_ID } from "@/lib/leads/student-classification";
import { PeriodFilter, usePeriodFilter } from "@/components/admin/PeriodFilter";
import { AdminGate } from "@/components/admin/AdminGate";

import {
  CAPTURE_FLOWS,
  POS_LPS,
  CRM_ROUTING_LABELS,
  tagsForFlow,
} from "@/lib/leads/capture-flow-catalog";
import { DISCARDED_REASON } from "@/lib/leads/qualification";
import { retryLeadDelivery } from "@/lib/admin/retry-delivery.functions";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { neutralizeCsvValue } from "@/lib/csv-safe";
import { UTM_CSV_HEADERS, utmCsvValues } from "@/lib/leads/utm-columns";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/admin/leads")({
  head: () => ({
    meta: [
      { title: "Admin — Leads" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => <AdminGate route="/admin/leads"><LeadsPage /></AdminGate>,
});

type Lead = {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  crm: string | null;
  pos_recomendada: string;
  pos_nome: string;
  pos_url: string;
  score_total: number;
  respostas: Array<{ pergunta: string; resposta: string }>;
  utm_source: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  session_id?: string | null;
  landing_path?: string | null;
  device?: string | null;

  origem: string;
  area: string | null;
  classificacao: string | null;
  delivered_to_crm: boolean;
  delivered_to_rdstation: boolean;
  crm_deal_id: string | null;
  crm_stage_id: string | null;
  crm_seller_email: string | null;
  crm_error: string | null;
  rdstation_error: string | null;
  created_at: string;
};

// Stage ID do funil de estudantes (definido em rdcrm.server.ts).
// Mantido aqui só para rotular "UNIVERSITÁRIOS" vs "FUNIL-POS" no painel — sem segredos.
const STAGE_ID_ESTUDANTE = RDCRM_STUDENT_STAGE_ID;
function funilLabel(stageId: string | null) {
  if (!stageId) return null;
  if (stageId === STAGE_ID_ESTUDANTE)
    return { label: "UNIVERSITÁRIOS" };
  return { label: "FUNIL-POS" };
}


function perfilLead(l: { respostas?: Array<{ pergunta: string; resposta: string }> | null }) {
  const r = (l.respostas ?? []).find((x) => /forma|gradua|estudante|universit/i.test(x.pergunta ?? ""));
  if (!r?.resposta) return null;
  return /estudante|cursando|universit|semestre|per[ií]odo/i.test(r.resposta) ? "ESTUDANTE" : "FORMADO";
}

function classifBadge(c: string | null | undefined) {
  const v = (c ?? "").toLowerCase();
  // Mesmo padrão visual do badge "enviado" (borda + fundo translúcido + texto colorido),
  // com uma cor distinta por temperatura.
  if (v === "quente") return { label: "🔥 Quente", cls: "border-orange-400/30 bg-orange-400/10 text-orange-400" };
  if (v === "morno") return { label: "🌤️ Morno", cls: "border-yellow-400/30 bg-yellow-400/10 text-yellow-300" };
  if (v === "frio") return { label: "❄️ Frio", cls: "border-sky-400/30 bg-sky-400/10 text-sky-400" };
  return { label: "—", cls: "border-border bg-muted text-muted-foreground" };
}

function RetryButton({ leadId, target }: { leadId: string; target: "rdstation" | "crm" }) {
  const retry = useServerFn(retryLeadDelivery);
  const qc = useQueryClient();
  const [loading, setLoading] = useState(false);
  return (
    <button
      type="button"
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          const res = await retry({ data: { lead_id: leadId, target } });
          if (res.ok) {
            toast.success(`Reenviado com sucesso (${target === "rdstation" ? "RD Station" : "CRM"})`);
          } else {
            toast.error(`Falha ao reenviar: ${res.error ?? "erro desconhecido"}`);
          }
          await qc.invalidateQueries({ queryKey: ["leads"] });
        } catch (e) {
          toast.error(e instanceof Error ? e.message : "Erro inesperado");
        } finally {
          setLoading(false);
        }
      }}
      className="inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
      title={`Reenviar para ${target === "rdstation" ? "RD Station" : "CRM"}`}
    >
      <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
      {loading ? "reenviando…" : "reenviar"}
    </button>
  );
}

function effectiveClassificacao(l: Lead): string | null | undefined {
  // Pré-checkout é sempre tratado como QUENTE (alta intenção de compra),
  // mesmo se o e-mail/telefone tiver histórico de estudante em outra captura.
  if (String(l.origem ?? "").startsWith("pre-checkout")) return "quente";
  // Confia no valor gravado — a regra atual (qualifyByAnswers em
  // src/lib/leads/qualification.ts) já retorna MORNO para estudantes 5º/
  // formando com intenção próxima (funil UNIVERSITÁRIOS).
  return l.classificacao;
}

// Nomenclatura interna (admin) — mapeia slug/nome da pós para label curto.
// Não altera o que é enviado para RD/CRM nem o que aparece nas páginas públicas.
// Nomenclatura interna padronizada (admin) — não afeta páginas públicas nem o
// que é enviado para RD/CRM. Mapeia qualquer slug/nome para um label curto.
const POS_LABELS: Record<string, string> = {
  orto: "POS-ORTO",
  ortopedia: "POS-ORTO",
  trauma: "POS-TRAUMA",
  traumato: "POS-TRAUMA",
  traumatologia: "POS-TRAUMA",
  esporte: "POS-ESPORTE",
  dor: "POS-DOR",
  geronto: "POS-GERONTO",
  gerontologia: "POS-GERONTO",
  prescricao: "POS-PRESCRICAO",
  prescrição: "POS-PRESCRICAO",
  presc: "POS-PRESCRICAO",
  todas: "POS-TODAS",
  combo: "POS-TODAS",
  feo: "POS-TODAS",
};

/**
 * Consolida slugs equivalentes usados em fluxos diferentes.
 * Ex.: a LP /aulas-experimentais-gerontologia usa slug "gerontologia",
 * enquanto ementas/pré-checkout usam "geronto". No admin ambos são POS-GERONTO.
 * Mantém URLs/tags públicas intactas — só normaliza aqui pra consolidar dados.
 */
export function canonicalSlug(slug: string | null | undefined): string {
  const s = (slug ?? "").trim().toLowerCase();
  if (s === "gerontologia") return "geronto";
  if (s === "ortopedia") return "orto";
  if (s === "traumato" || s === "traumatologia") return "trauma";
  if (s === "prescrição" || s === "presc") return "prescricao";
  if (s === "todas" || s === "feo" || s === "pos-todas") return "combo";
  return s;
}

/**
 * Normaliza a `origem` (tag/identificador) do lead trocando o slug pela versão canônica.
 * Ex.: `aulas-exp-gerontologia` → `aulas-exp-geronto`, `aulas-exp-gerontologia-parcial` → `aulas-exp-geronto-parcial`.
 */
export function canonicalOrigem(origem: string | null | undefined): string {
  const o = (origem ?? "").trim().toLowerCase();
  if (!o) return "";
  const prefixes = ["aulas-exp-", "ementa-lp-", "ementa-site-", "pre-checkout-gg-", "pre-checkout-", "checkout-"];
  for (const p of prefixes) {
    if (o.startsWith(p)) {
      const rest = o.slice(p.length);
      const parcialMatch = rest.match(/^(.+?)(-parcial)?$/);
      if (!parcialMatch) return o;
      const slug = canonicalSlug(parcialMatch[1]);
      return `${p}${slug}${parcialMatch[2] ?? ""}`;
    }
  }
  return o;
}

function posLabel(slug: string | null | undefined, fallbackNome?: string | null) {
  const k = (slug ?? "").trim().toLowerCase();
  if (k && POS_LABELS[k]) return POS_LABELS[k];
  // Fallback: tenta inferir a partir do nome completo (legado)
  const n = (fallbackNome ?? "").toLowerCase();
  if (n.includes("ortopedia")) return "POS-ORTO";
  if (n.includes("trauma")) return "POS-TRAUMA";
  if (n.includes("esporte")) return "POS-ESPORTE";
  if (n.includes("geronto")) return "POS-GERONTO";
  if (n.includes("dor")) return "POS-DOR";
  if (n.includes("presc")) return "POS-PRESCRICAO";
  if (n.includes("todas") || n.includes("combo") || n.includes("feo")) return "POS-TODAS";
  return (slug || fallbackNome || "—").toString();
}

/**
 * Deriva a tag do RD Station a partir da origem real do lead.
 * Reflete o que é enviado no payload de cada fluxo (aulas exp / ementas / pré-checkout).
 */
function rdTagFor(origem: string | null | undefined, area: string | null | undefined) {
  const o = (origem ?? "").trim().toLowerCase();
  if (o) return o; // já vem no formato final (`pre-checkout-orto`, `ementa-site-orto`, etc.)
  const a = (area ?? "").trim().toLowerCase();
  return a ? `aulas-exp-${a}` : "aulas-exp";
}

const SLUG_DISPLAY: Record<string, string> = {
  orto: "Orto",
  ortopedia: "Orto",
  trauma: "Trauma",
  traumato: "Trauma",
  traumatologia: "Trauma",
  esporte: "Esporte",
  dor: "Dor",
  geronto: "Geronto",
  gerontologia: "Geronto",
  prescricao: "Prescrição",
  "prescrição": "Prescrição",
  combo: "Todas",
  todas: "Todas",
  feo: "Todas",
  "pos-todas": "Todas",
};

function extractSlug(origem: string | null | undefined, area: string | null | undefined): string {
  const a = (area ?? "").trim().toLowerCase();
  if (a) return a;
  const o = (origem ?? "").toLowerCase();
  let m;
  if ((m = o.match(/^aulas-exp-(.+)$/))) return m[1];
  if ((m = o.match(/^aulas?-experimentais?-(.+)$/))) return m[1];
  if ((m = o.match(/^ementa-lp-(.+)$/))) return m[1];
  if ((m = o.match(/^ementa-site-(.+)$/))) return m[1];
  if ((m = o.match(/^ementa-(.+?)-site$/))) return m[1];
  if ((m = o.match(/^pre-checkout-gg-(.+?)(?:-.*)?$/))) return m[1];
  if ((m = o.match(/^pre-checkout-(.+?)(?:-.*)?$/))) return m[1];
  if ((m = o.match(/^aprovafeo-(.+)$/))) return m[1];

  if ((m = o.match(/^checkout-(.+)$/))) return m[1];
  return "";
}

function slugDisplay(slug: string): string {
  const k = (slug ?? "").trim().toLowerCase();
  if (SLUG_DISPLAY[k]) return SLUG_DISPLAY[k];
  return k ? k.charAt(0).toUpperCase() + k.slice(1) : "";
}

/** Deriva a URL pública (ou embed) da rota associada ao lead. */
function urlForOrigem(origem: string | null | undefined, area: string | null | undefined): string {
  const o = (origem ?? "").toLowerCase();
  const slug = extractSlug(origem, area);
  if (o === "black26-lista") return "/black-lista";
  if (o === "black26-lista-entregas") return "/black-lista-entregas";
  if (o === "black26-lista-quizz") return "/black-quizz";
  if (o === "revista" || o.startsWith("revista-")) return "/revista";
  if (o === "imersao-pbe" || o.startsWith("imersao-pbe")) return "/imersao-pbe";
  if (o === "funil-lca") return "/funil-lca";
  if (o === "funil-tmanual") return "/funil-tmanual";
  if (o === "funil-sdfp") return "/funil-sdfp";
  if (o === "funil-us") return "/funil-us";
  if (o === "quizz-dor") return "/quizz-dor-pos-todas";
  if (o === "quizz-pos") return "/quizz-pos-todas";
  if (o === "fisiocompleto" || o.startsWith("fisiocompleto")) return "/fisiocompleto";
  if (o === "captura-cert-quadril" || o.startsWith("captura-cert-quadril")) return "/certificacao-quadril";

  if (o.startsWith("aprovafeo")) return `/aprovafeo-${slug || "orto"}`;
  if (o.startsWith("aulas-exp") || o.startsWith("aulas-experimentais") || o.startsWith("aula-exp")) {
    const s = slug === "geronto" ? "gerontologia" : slug === "combo" ? "pos-todas" : slug;
    return s ? `/aulas-experimentais-${s}` : "/";
  }
  if (o.startsWith("ementa-lp-")) return slug ? `/ementa-${slug}` : "/";
  if (o.startsWith("ementa-")) return slug ? `/embed/ementa/${slug}` : "/";
  if (o.startsWith("pre-checkout-gg-")) return slug ? `/embed/pre-checkout-gg/${slug}` : "/";
  if (o.startsWith("pre-checkout-") || o.startsWith("checkout-")) return slug ? `/embed/pre-checkout/${slug}` : "/";
  return "/";
}

/**
 * Cor-tema da tag de origem por pós (alinhado com POS_THEMES em posfigma/theme.ts).
 * Aplicada em Aulas Exp / Ementa / Pré-checkout (fluxos ligados a uma pós).
 */
const POS_TAG_CLS: Record<string, string> = {
  orto: "border-teal-500/30 bg-teal-500/10 text-teal-300",
  trauma: "border-yellow-500/30 bg-yellow-500/10 text-yellow-300",
  esporte: "border-red-500/30 bg-red-500/10 text-red-300",
  dor: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300",
  geronto: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  prescricao: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  combo: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
};
// Cor única para todos os leads gratuitos / outras ações sem pós definida
// (Revista, AprovaFEO, Quizz, etc.)
const GRATUITOS_TAG_CLS = "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300";

function posTagCls(slug: string): string {
  const s = canonicalSlug(slug);
  return POS_TAG_CLS[s] ?? GRATUITOS_TAG_CLS;
}

/**
 * Classifica a origem de forma legível — pós-área em UPPERCASE
 * (ex.: "Aulas Exp ORTO", "Ementa GERONTO", "Pré-chk ESPORTE").
 */
function origemKind(origem: string | null | undefined, area?: string | null | undefined) {
  const o = (origem ?? "").toLowerCase();
  const slug = extractSlug(origem, area);
  const name = slugDisplay(slug).toUpperCase();
  if (o === "black26-lista-quizz") {
    return { label: "Black Quizz", cls: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400" };
  }
  if (o === "black26-lista-entregas") {
    return { label: "Black Entregas", cls: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400" };
  }
  if (o === "black26-lista") {
    return { label: "Black Lista", cls: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-400" };
  }
  if (o.startsWith("pre-checkout-gg-")) {
    return { label: name ? `Pré-chk GG ${name}` : "Pré-chk GG", cls: posTagCls(slug) };
  }
  if (o.startsWith("checkout-") || o.startsWith("pre-checkout-")) {
    return { label: name ? `Pré-chk ${name}` : "Pré-chk", cls: posTagCls(slug) };
  }
  if (o === "quizz-dor" || o === "quizz-pos") {
    return { label: o === "quizz-dor" ? "Quizz Dor" : "Quizz Pós", cls: GRATUITOS_TAG_CLS };
  }
  if (o.startsWith("ementa-lp-")) {
    return { label: name ? `Ementa LP ${name}` : "Ementa LP", cls: posTagCls(slug) };
  }
  if (o.startsWith("ementa-")) {
    return { label: name ? `Ementa ${name}` : "Ementa", cls: posTagCls(slug) };
  }
  if (o.startsWith("aprovafeo-") || o === "aprovafeo") {
    return { label: "AprovaFEO", cls: GRATUITOS_TAG_CLS };
  }
  if (o.startsWith("aulas-experimentais") || o.startsWith("aula-exp") || o.startsWith("aulas-exp")) {
    return { label: name ? `Aulas Exp ${name}` : "Aula Exp.", cls: posTagCls(slug) };
  }
  if (o === "revista") {
    return { label: "Revista", cls: GRATUITOS_TAG_CLS };
  }
  if (o === "imersao-pbe" || o.startsWith("imersao-pbe")) {
    return { label: "Imersão PBE", cls: GRATUITOS_TAG_CLS };
  }
  if (o === "funil-lca") return { label: "Funil LCA", cls: GRATUITOS_TAG_CLS };
  if (o === "funil-tmanual") return { label: "Funil T.Manual", cls: GRATUITOS_TAG_CLS };
  if (o === "funil-sdfp") return { label: "Funil SDFP", cls: GRATUITOS_TAG_CLS };
  if (o === "funil-us") return { label: "Funil US", cls: GRATUITOS_TAG_CLS };
  if (o.startsWith("fisiocompleto")) {
    return { label: o.endsWith("-parcial") ? "Fisio Completo (parcial)" : "Fisio Completo", cls: posTagCls("combo") };
  }
  if (o.startsWith("captura-cert-quadril")) {
    return { label: "Cert. Quadril", cls: GRATUITOS_TAG_CLS };
  }
  if (!o) return { label: "—", cls: "border-border bg-muted text-muted-foreground" };
  return { label: o, cls: GRATUITOS_TAG_CLS };
}


type LeadTipo = "aulas-exp" | "ementa-site" | "ementa-lp" | "pre-checkout" | "quizzes" | "gratuitos" | "outros";

const TIPO_LABELS: Record<LeadTipo, string> = {
  "aulas-exp": "LEAD AULAS EXP",
  "ementa-site": "LEAD EMENTA-SITE",
  "ementa-lp": "LEAD EMENTA-LP (VERSÃO D)",
  "pre-checkout": "LEAD PRÉ-CHECKOUT",
  "quizzes": "LEAD QUIZZES",
  "gratuitos": "LEADS GRATUITOS",
  "outros": "OUTROS",
};

function leadTipo(origem: string | null | undefined): LeadTipo {
  const o = (origem ?? "").toLowerCase();
  if (o.startsWith("aulas-exp") || o.startsWith("aulas-experimentais") || o.startsWith("aula-exp")) return "aulas-exp";
  if (o.startsWith("ementa-lp-")) return "ementa-lp";
  if (o.startsWith("ementa-")) return "ementa-site";
  if (o.startsWith("pre-checkout") || o.startsWith("checkout-")) return "pre-checkout";
  if (o.startsWith("quizz-")) return "quizzes";
  if (o === "revista" || o.startsWith("revista-")) return "gratuitos";
  if (o === "imersao-pbe" || o.startsWith("imersao-pbe")) return "gratuitos";
  if (o === "funil-lca" || o === "funil-tmanual" || o === "funil-sdfp" || o === "funil-us") return "gratuitos";
  if (o.startsWith("fisiocompleto") || o.startsWith("captura-cert-quadril")) return "gratuitos";
  return "outros";

}

/**
 * Mapeia um path público (page_views.path) para o slug de pós associado.
 * Retorna null se o path não faz parte de nenhum funil de captura.
 */
function pathToSlug(path: string): string | null {
  const p = (path || "").toLowerCase().replace(/\/+$/, "");
  let m: RegExpMatchArray | null;
  if ((m = p.match(/^\/aulas-experimentais-(.+)$/))) {
    const s = m[1];
    if (s === "gerontologia") return "geronto";
    if (s === "pos-todas") return "combo";
    return s;
  }
  if ((m = p.match(/^\/embed\/ementa\/(.+)$/))) return m[1];
  if ((m = p.match(/^\/embed\/pre-checkout-gg\/(.+)$/))) return m[1];
  if ((m = p.match(/^\/embed\/pre-checkout\/(.+)$/))) return m[1];

  if ((m = p.match(/^\/(orto|dor|esporte|geronto|trauma|prescricao)[12]?$/))) return m[1];
  if (p === "/fisiocompleto" || p === "/fisiocompleto-venda" || p === "/fisiocompleto-wpp") return "combo";
  return null;
}

/**
 * Mapeia path → origem canônica usada em leads.origem.
 * Para LPs de venda (/orto1 etc.) devolve `ementa-site-{slug}` (o form principal ali é o lead-ementa).
 */
function pathToOrigem(path: string): string | null {
  const p = (path || "").toLowerCase().replace(/\/+$/, "");
  if (p === "/revista" || p === "/revista-star") return "revista";
  if (p === "/imersao-pbe" || p === "/imersao-pbe-star") return "imersao-pbe";
  if (p === "/funil-lca" || p === "/funil-lca-combo-imersoes") return "funil-lca";
  if (p === "/funil-tmanual" || p === "/funil-tmanual-combo-imersoes") return "funil-tmanual";
  if (p === "/funil-sdfp" || p === "/funil-sdfp-combo-imersoes") return "funil-sdfp";
  if (p === "/funil-us" || p === "/funil-us-combo-imersoes") return "funil-us";
  if (p === "/quizz-dor-pos-todas") return "quizz-dor";
  if (p === "/quizz-pos-todas") return "quizz-pos";
  if (p === "/fisiocompleto" || p === "/fisiocompleto-venda" || p === "/fisiocompleto-wpp") return "fisiocompleto";
  if (p === "/certificacao-quadril") return "captura-cert-quadril";

  const slug = pathToSlug(p);
  if (!slug) return null;
  if (p.startsWith("/aulas-experimentais-")) return `aulas-exp-${slug}`;
  if (p.startsWith("/embed/pre-checkout-gg/")) return `pre-checkout-gg-${slug}`;
  if (p.startsWith("/embed/pre-checkout/")) return `pre-checkout-${slug}`;

  if (p.startsWith("/embed/ementa/")) return `ementa-site-${slug}`;
  // LPs de venda no site (/orto1, /dor2, /fisiocompleto...) => a captura ali é o lead-ementa
  return `ementa-site-${slug}`;
}

function pathToTipo(path: string): LeadTipo | null {
  const origem = pathToOrigem(path);
  if (!origem) return null;
  return leadTipo(origem);
}



/**
 * Reproduz no admin as tags enviadas ao RD Station por cada fluxo:
 * tag específica + tag geral + tag de qualificação (frio/morno/quente).
 */
function rdTagsFor(
  origem: string | null | undefined,
  area: string | null | undefined,
  classificacao: string | null | undefined,
): string[] {
  const o = (origem ?? "").toLowerCase();
  const slug = extractSlug(origem, area);
  const cls = (classificacao ?? "").toLowerCase();
  const qual = cls === "quente" || cls === "morno" || cls === "frio" ? `lead-${cls}` : null;
  const tags: string[] = [];

  // AprovaFEO é uma ação isolada — envia APENAS a tag exata da campanha
  // (ex.: `aprovafeo-orto26`), sem tag geral `-todas` nem qualificação.
  if (o.startsWith("aprovafeo-") || o === "aprovafeo") {
    if (o) tags.push(o);
    return tags;
  }

  if (o === "black26-lista" || o === "black26-lista-entregas" || o === "black26-lista-quizz") {
    tags.push(o);
  } else if (o === "quizz-dor" || o === "quizz-pos") {
    tags.push(o);
  } else if (o === "revista" || o.startsWith("revista-")) {
    tags.push("revista");
  } else if (o === "imersao-pbe" || o.startsWith("imersao-pbe")) {
    tags.push("imersao-pbe");
  } else if (o === "funil-lca" || o === "funil-tmanual" || o === "funil-sdfp" || o === "funil-us") {
    tags.push(o);
  } else if (o.startsWith("fisiocompleto")) {
    tags.push("fisiocompleto");
  } else if (o.startsWith("captura-cert-quadril")) {
    tags.push("captura-cert-quadril");

  } else if (o.startsWith("ementa-lp-")) {
    if (slug) tags.push(`ementa-lp-${slug}`);
    tags.push("ementa-lp-todas");
  } else if (o.startsWith("ementa-")) {
    if (slug) tags.push(`ementa-site-${slug}`);
    tags.push("ementa-site-todas");
  } else if (o.startsWith("pre-checkout-gg-")) {
    if (slug) tags.push(`pre-checkout-gg-${slug}`);
    tags.push("pre-checkout-gg-todas");
  } else if (o.startsWith("checkout-") || o.startsWith("pre-checkout-")) {
    if (slug) tags.push(`pre-checkout-${slug}`);
    tags.push("pre-checkout-todas");
  } else if (o.startsWith("aulas-exp") || o.startsWith("aulas-experimentais") || o.startsWith("aula-exp")) {
    if (slug) tags.push(slug === "combo" ? "aulas-exp-combo" : `aulas-exp-${slug}`);
    tags.push("aulas-exp-todas");
  }
  if (qual) tags.push(qual);
  return tags;
}

function waLink(telefone: string, nome: string) {
  const digits = (telefone ?? "").replace(/\D/g, "");
  const withCountry = digits.length <= 11 ? `55${digits}` : digits;
  const msg = encodeURIComponent(`Olá ${nome.split(" ")[0]}, aqui é da Fisio em Ortopedia!`);
  return `https://wa.me/${withCountry}?text=${msg}`;
}

/**
 * Rótulo amigável exibido na coluna "Compras". A tag interna do produto
 * (slug curto vindo de `productToTag`) é convertida em algo legível pro time,
 * seguindo o padrão "PRODUTO - FONTE" (ex.: "POS-ORTO - CRM", "Star Cursos - MKT").
 */
const PURCHASE_PRODUCT_LABELS: Record<string, string> = {
  "pos-orto": "POS-ORTO",
  "pos-dor": "POS-DOR",
  "pos-esporte": "POS-ESPORTE",
  "pos-geronto": "POS-GERONTO",
  "pos-trauma": "POS-TRAUMA",
  "pos-prescricao": "POS-PRESCRICAO",
  "ext-pos-orto": "Extensão POS-ORTO",
  "ext-pos-dor": "Extensão POS-DOR",
  "ext-pos-esporte": "Extensão POS-ESPORTE",
  "ext-pos-geronto": "Extensão POS-GERONTO",
  "ext-pos-trauma": "Extensão POS-TRAUMA",
  "ext-pos-prescricao": "Extensão POS-PRESCRICAO",
  "passaporte-feo": "Passaporte FEO",
  "pos-em-dobro": "Pós em Dobro",
  "combo-imersoes": "Combo Imersões",
  "cert-lombar": "Certificação Lombar",
  "cert-cervical": "Certificação Cervical",
  "cert-joelho": "Certificação Joelho",
  "cert-ombro": "Certificação Ombro",
  "cert-tornozelo-pe": "Certificação Tornozelo/Pé",
  "cert-eletrofisicos": "Certificação Eletrofísicos",
  "cert-outra": "Certificação",
  "prova-orto": "Prova POS-ORTO",
  "prova-dor": "Prova POS-DOR",
  "prova-esporte": "Prova POS-ESPORTE",
  "prova-geronto": "Prova POS-GERONTO",
  "prova-trauma": "Prova POS-TRAUMA",
  "prova": "Prova",
  "imersao-lombar": "Imersão Lombar",
  "imersao-dor": "Imersão Dor",
  "imersao": "Imersão",
  "cbfeo": "CBFEO",
  "star": "Star Cursos",
  "fisioemp": "FisioEmp",
  "agente-ia": "Agente IA",
  "ebook": "E-book",
};

function purchaseProductLabel(tag: string): string {
  return PURCHASE_PRODUCT_LABELS[tag] ?? tag.toUpperCase();
}

/**
 * Cor-tema por produto na coluna Compras e no Histórico:
 *  - 6 pós (e extensões) → mesma paleta de POS_TAG_CLS
 *  - 6 certificações → paleta dedicada (uma cor por região anatômica)
 *  - STAR Cursos → âmbar
 *  - Demais produtos (ebook, agente IA, imersão, cbfeo, fisioemp, provas, combos…) → fuchsia genérico
 */
const CERT_TAG_CLS: Record<string, string> = {
  "cert-lombar": "border-rose-500/30 bg-rose-500/10 text-rose-300",
  "cert-cervical": "border-pink-500/30 bg-pink-500/10 text-pink-300",
  "cert-joelho": "border-lime-500/30 bg-lime-500/10 text-lime-300",
  "cert-ombro": "border-orange-500/30 bg-orange-500/10 text-orange-300",
  "cert-tornozelo-pe": "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  "cert-eletrofisicos": "border-blue-500/30 bg-blue-500/10 text-blue-300",
};
const STAR_TAG_CLS = "border-amber-500/30 bg-amber-500/10 text-admin-accent";

function purchaseTagCls(tag: string): string {
  if (tag === "star") return STAR_TAG_CLS;
  if (CERT_TAG_CLS[tag]) return CERT_TAG_CLS[tag];
  const posSlug = tag.replace(/^ext-/, "").replace(/^pos-/, "");
  if (POS_TAG_CLS[posSlug]) return POS_TAG_CLS[posSlug];
  return GRATUITOS_TAG_CLS;
}

const CHANNEL_CLS: Record<"CRM" | "MKT", string> = {
  CRM: "border-purple-500/30 bg-purple-500/10 text-purple-300",
  MKT: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
};


function LeadsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [authReady, setAuthReady] = useState(false);
  const [authed, setAuthed] = useState(false);

  // Aguarda hidratação da sessão antes de chamar server fn protegida
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (!data.session) {
        navigate({ to: "/admin" });
        return;
      }
      setAuthed(true);
      setAuthReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: "/admin" });
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  const checkAdminFn = useServerFn(checkIsAdmin);
  const listFn = useServerFn(listLeads);
  const deleteFn = useServerFn(deleteLead);


  const adminQuery = useQuery({
    queryKey: ["isAdmin"],
    queryFn: () => checkAdminFn(),
    enabled: authReady && authed,
    retry: false,
  });

  const leadsQuery = useQuery({
    queryKey: ["leads"],
    queryFn: () => listFn(),
    enabled: !!adminQuery.data?.isAdmin,
    retry: false,
  });


  const [search, setSearch] = useState("");
  const [posFilter, setPosFilter] = useState<string>("all");
  const [originFilter, setOriginFilter] = useState<string>("all");
  const [tipoFilter, setTipoFilter] = useState<string>("all");
  const [tempFilter, setTempFilter] = useState<string>("all");
  const [sellerFilter, setSellerFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const period = usePeriodFilter("today");

  const sessionsFn = useServerFn(getSessionsByPath);
  const purchasesFn = useServerFn(listPurchasesByEmail);
  const attributedFn = useServerFn(listAttributedPurchases);
  const purchasesQuery = useQuery({
    queryKey: ["purchases-by-email"],
    queryFn: () => purchasesFn(),
    enabled: !!adminQuery.data?.isAdmin,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const attributedQuery = useQuery({
    queryKey: ["purchases-attributed"],
    queryFn: () => attributedFn(),
    enabled: !!adminQuery.data?.isAdmin,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const sessionsQuery = useQuery({
    queryKey: ["sessions-by-path", period.range.from, period.range.to],
    queryFn: () =>
      sessionsFn({
        data: {
          fromIso: period.range.from ? new Date(period.range.from).toISOString() : undefined,
          toIso: period.range.to ? new Date(period.range.to).toISOString() : undefined,
        },
      }),
    enabled: !!adminQuery.data?.isAdmin,
    retry: false,
    staleTime: 60_000,
  });
  type SortKey = "created_at" | "nome" | "origem" | "pos_nome" | "score_total" | "classificacao";
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [pageSize, setPageSize] = useState<number>(30);
  const [page, setPage] = useState<number>(0);
  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "created_at" || k === "score_total" ? "desc" : "asc"); }
  };
  const sortIndicator = (k: SortKey) => sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const leads = (leadsQuery.data?.leads as Lead[] | undefined) ?? [];

  const purchasesByEmail = (purchasesQuery.data?.byEmail ?? {}) as Record<string, PurchaseTag[]>;
  const purchasesByPhone = (purchasesQuery.data?.byPhone ?? {}) as Record<string, PurchaseTag[]>;
  const histByEmail = (purchasesQuery.data?.histByEmail ?? {}) as Record<string, string[]>;
  const attributedByLeadId = (attributedQuery.data?.byLeadId ?? {}) as Record<string, PurchaseTag[]>;
  const attributedTotalByLeadId = (attributedQuery.data?.totalByLeadId ?? {}) as Record<string, number>;

  // Histórico consolidado por e-mail: todas as conversões (capturas) e compras
  // do lead, ordenadas do mais recente para o mais antigo. Alimenta o bloco
  // "Histórico completo" exibido ao expandir uma linha.
  type HistoryEvent =
    | { kind: "capture"; ts: number; date: string; lpLabel: string; lpCls: string; tags: string[] }
    | { kind: "purchase"; ts: number; date: string; productLabel: string; productCls: string; channel: "CRM" | "MKT" };
  const historyByEmail = useMemo(() => {
    const map = new Map<string, HistoryEvent[]>();
    for (const l of leads) {
      const key = (l.email ?? "").trim().toLowerCase();
      if (!key) continue;
      const ts = new Date(l.created_at).getTime();
      const arr = map.get(key) ?? [];
      const kind = origemKind(l.origem, l.area);
      arr.push({
        kind: "capture",
        ts: isNaN(ts) ? 0 : ts,
        date: l.created_at,
        lpLabel: kind.label,
        lpCls: kind.cls,
        tags: rdTagsFor(l.origem, l.area, effectiveClassificacao(l)),
      });
      map.set(key, arr);
    }
    for (const [email, purchases] of Object.entries(purchasesByEmail)) {
      const key = email.trim().toLowerCase();
      if (!key) continue;
      const arr = map.get(key) ?? [];
      for (const p of purchases) {
        const ts = p.paid_at ? new Date(p.paid_at).getTime() : 0;
        arr.push({
          kind: "purchase",
          ts: isNaN(ts) ? 0 : ts,
          date: p.paid_at ?? "",
          productLabel: purchaseProductLabel(p.tag),
          productCls: purchaseTagCls(p.tag),
          channel: p.channel,
        });
      }
      map.set(key, arr);
    }
    // Ordem cronológica ASCENDENTE: mais antiga em cima, mais recente embaixo.
    for (const arr of map.values()) arr.sort((a, b) => a.ts - b.ts);
    return map;
  }, [leads, purchasesByEmail]);


  const { fromTs, toTs } = useMemo(() => ({
    fromTs: period.range.from ? new Date(period.range.from).getTime() : 0,
    toTs: period.range.to ? new Date(period.range.to).getTime() : Date.now(),
  }), [period.range]);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      const ts = new Date(l.created_at).getTime();
      if (ts < fromTs || ts > toTs) return false;
      if (posFilter !== "all" && canonicalSlug(l.pos_recomendada) !== posFilter) return false;
      if (originFilter !== "all" && canonicalOrigem(l.origem) !== originFilter) return false;
      if (tipoFilter !== "all" && leadTipo(canonicalOrigem(l.origem)) !== tipoFilter) return false;
      if (tempFilter !== "all") {
        const c = (effectiveClassificacao(l) ?? "").toLowerCase();
        if (c !== tempFilter) return false;
      }
      if (sellerFilter !== "all") {
        const stored = l.crm_seller_email?.toLowerCase() ?? "";
        if (stored !== sellerFilter.toLowerCase()) return false;
      }

      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          l.nome.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q) ||
          l.telefone.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [leads, search, posFilter, originFilter, tipoFilter, tempFilter, sellerFilter, fromTs, toTs]);

  const kpis = useMemo(() => {
    let total = 0, quente = 0, morno = 0, frio = 0, parcial = 0;
    for (const l of filtered) {
      total += 1;
      const c = (effectiveClassificacao(l) ?? "").toLowerCase();
      if (c === "quente") quente += 1;
      else if (c === "morno") morno += 1;
      else if (c === "frio") frio += 1;
      if (!l.respostas || l.respostas.length === 0) parcial += 1;
    }
    return { total, quente, morno, frio, parcial };
  }, [filtered]);

  // Funil: SESSÕES → INICIARAM FORM → LEADS CAPTURADOS.
  // - Sessões: distinct session_id em page_views (path bate com o escopo filtrado)
  // - Iniciaram form: todos os leads do período no escopo (parcial+completo)
  // - Leads capturados: só completos (respostas > 0)
  const sessionsByPath = (sessionsQuery.data ?? []) as Array<{ path: string; sessions: number }>;
  const funnel = useMemo(() => {
    const pathMatchesScope = (path: string): boolean => {
      const origem = pathToOrigem(path);
      const tipo = pathToTipo(path);
      if (!origem) return false;
      if (tipoFilter !== "all" && tipo !== tipoFilter) return false;
      if (originFilter !== "all" && canonicalOrigem(origem) !== originFilter) return false;
      if (posFilter !== "all") {
        const slug = pathToSlug(path);
        if (canonicalSlug(slug) !== posFilter) return false;
      }
      return true;
    };
    let sessions = 0;
    const perOrigemSessions = new Map<string, number>();
    for (const row of sessionsByPath) {
      if (!pathMatchesScope(row.path)) continue;
      sessions += row.sessions;
      const o = canonicalOrigem(pathToOrigem(row.path)!);
      perOrigemSessions.set(o, (perOrigemSessions.get(o) ?? 0) + row.sessions);
    }
    const started = kpis.total;
    const captured = kpis.total - kpis.parcial;
    return { sessions, started, captured, perOrigemSessions };
  }, [sessionsByPath, tipoFilter, originFilter, posFilter, kpis.total, kpis.parcial]);

  // Conversões: leads capturados (com respostas) do escopo cujo e-mail tem venda paga.
  const conversions = useMemo(() => {
    const emails = new Set<string>();
    for (const l of filtered) {
      if (l.respostas && l.respostas.length > 0) {
        const em = (l.email ?? "").trim().toLowerCase();
        if (em) emails.add(em);
      }
    }
    let total = 0, crm = 0, mkt = 0;
    for (const em of emails) {
      const ps = purchasesByEmail[em] ?? [];
      if (ps.length === 0) continue;
      total += 1;
      if (ps.some((p) => p.channel === "CRM")) crm += 1;
      else mkt += 1;
    }
    return { total, crm, mkt };
  }, [filtered, purchasesByEmail]);

  // Per-origem funnel para o gráfico de barras: sessions/started/captured por origem no escopo filtrado.
  const perOriginFunnel = useMemo(() => {
    const map = new Map<string, { origem: string; sessions: number; started: number; captured: number }>();
    for (const [origem, sessions] of funnel.perOrigemSessions) {
      map.set(origem, { origem, sessions, started: 0, captured: 0 });
    }
    for (const l of filtered) {
      const key = canonicalOrigem(l.origem);
      const cur = map.get(key) ?? { origem: key, sessions: 0, started: 0, captured: 0 };
      cur.started += 1;
      if (l.respostas && l.respostas.length > 0) cur.captured += 1;
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.sessions - a.sessions);
  }, [filtered, funnel.perOrigemSessions]);



  const classifRank: Record<string, number> = { quente: 3, morno: 2, frio: 1 };
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sortKey === "created_at") { av = new Date(a.created_at).getTime(); bv = new Date(b.created_at).getTime(); }
      else if (sortKey === "score_total") { av = a.score_total; bv = b.score_total; }
      else if (sortKey === "classificacao") { av = classifRank[(effectiveClassificacao(a) ?? "").toLowerCase()] ?? 0; bv = classifRank[(effectiveClassificacao(b) ?? "").toLowerCase()] ?? 0; }
      else { av = (a[sortKey] ?? "").toString().toLowerCase(); bv = (b[sortKey] ?? "").toString().toLowerCase(); }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Reset para primeira página quando filtros/ordenação/tamanho mudam.
  useEffect(() => { setPage(0); }, [search, posFilter, originFilter, tipoFilter, tempFilter, sellerFilter, fromTs, toTs, sortKey, sortDir, pageSize]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pageStart = currentPage * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, sorted.length);
  const paged = useMemo(() => sorted.slice(pageStart, pageEnd), [sorted, pageStart, pageEnd]);

  const posOptions = useMemo(() => {
    const set = new Set(leads.map((l) => canonicalSlug(l.pos_recomendada)).filter(Boolean));
    return Array.from(set);
  }, [leads]);

  const originOptions = useMemo(() => {
    const set = new Set(leads.map((l) => canonicalOrigem(l.origem)).filter(Boolean));
    return Array.from(set);
  }, [leads]);

  const perOrigin = useMemo(() => {
    const map = new Map<string, { origem: string; quente: number; morno: number; frio: number; total: number }>();
    for (const l of filtered) {
      const key = canonicalOrigem(l.origem);
      const r = map.get(key) ?? { origem: key, quente: 0, morno: 0, frio: 0, total: 0 };
      const c = (effectiveClassificacao(l) ?? "").toLowerCase();
      if (c === "quente") r.quente += 1;
      else if (c === "morno") r.morno += 1;
      else if (c === "frio") r.frio += 1;
      r.total += 1;
      map.set(key, r);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [filtered]);

  const perDay = useMemo(() => {
    const map = new Map<string, { day: string; quente: number; morno: number; frio: number; total: number }>();
    for (const l of filtered) {
      const d = new Date(l.created_at).toISOString().slice(0, 10);
      const r = map.get(d) ?? { day: d, quente: 0, morno: 0, frio: 0, total: 0 };
      const c = (effectiveClassificacao(l) ?? "").toLowerCase();
      if (c === "quente") r.quente += 1;
      else if (c === "morno") r.morno += 1;
      else if (c === "frio") r.frio += 1;
      r.total += 1;
      map.set(d, r);
    }
    return Array.from(map.values())
      .sort((a, b) => a.day.localeCompare(b.day))
      .map((r) => ({ day: r.day.slice(5), Frio: r.frio, Morno: r.morno, Quente: r.quente, Total: r.total }));
  }, [filtered]);

  const perOriginChart = useMemo(
    () =>
      perOrigin.slice(0, 8).map((r) => ({
        origem: r.origem.length > 18 ? r.origem.slice(0, 17) + "…" : r.origem,
        Quente: r.quente,
        Morno: r.morno,
        Frio: r.frio,
      })),
    [perOrigin],
  );



  const handleDelete = async (id: string) => {
    if (!confirm("Excluir este lead?")) return;
    try {
      await deleteFn({ data: { id } });
      toast.success("Lead excluído");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao excluir");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/admin" });
  };


  const exportCSV = () => {
    const headers = [
      "Data",
      "Nome",
      "Email",
      "Telefone",
      "Pós",
      "Classificação",
      "Score",
      "RD ok",
      "Tag RD",
      "Erro RD",
      "CRM ok",
      "Funil CRM",
      "Deal ID",
      "Erro CRM",
      ...UTM_CSV_HEADERS,
      "Respostas",
    ];
    const rows = filtered.map((l) => [
      formatBR(l.created_at),
      l.nome,
      l.email,
      toE164BR(l.telefone),
      posLabel(l.pos_recomendada, l.pos_nome),
      effectiveClassificacao(l) ?? "",
      String(l.score_total),
      l.delivered_to_rdstation ? "sim" : "não",
      rdTagFor(l.origem, l.area),
      l.rdstation_error ?? "",
      l.delivered_to_crm ? "sim" : "não",
      l.crm_stage_id ? (l.crm_stage_id === STAGE_ID_ESTUDANTE ? "UNIVERSITÁRIOS" : "FUNIL-POS") : "",
      l.crm_deal_id ?? "",
      l.crm_error ?? "",
      ...utmCsvValues({
        utm_source: l.utm_source,
        utm_medium: l.utm_medium,
        utm_campaign: l.utm_campaign,
        utm_content: l.utm_content,
        utm_term: l.utm_term,
      }),
      (l.respostas ?? [])
        .map((r) => `${r.pergunta}: ${r.resposta}`)
        .join(" | "),
    ]);

    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${neutralizeCsvValue(String(c ?? "")).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authReady) {
    return <div className="p-10 text-center text-muted-foreground">Carregando...</div>;
  }

  if (adminQuery.isLoading) {
    return <div className="p-10 text-center text-muted-foreground">Verificando acesso...</div>;
  }

  if (adminQuery.data && !adminQuery.data.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-bold">Acesso negado</h1>
          <p className="text-muted-foreground text-sm">
            Sua conta está autenticada mas não tem permissão de admin. Peça pra um admin
            adicionar seu user_id na tabela <code>user_roles</code> com o papel{" "}
            <code>admin</code>.
          </p>
          <p className="text-xs text-muted-foreground break-all">
            Seu user_id: <code>{adminQuery.data.userId}</code>
          </p>
          <Button onClick={handleLogout} variant="outline">
            <LogOut className="h-4 w-4 mr-2" /> Sair
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <header className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div className="min-w-0">
            <Button asChild variant="outline" size="sm" className="mb-3">
              <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar ao menu</Link>
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold truncate">Leads capturados</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {filtered.length} de {leads.length} lead(s) no período
            </p>
          </div>
          <div className="grid grid-cols-3 sm:flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              disabled={leadsQuery.isFetching}
              onClick={async () => {
                try {
                  await queryClient.invalidateQueries({ queryKey: ["leads"] });
                  await queryClient.invalidateQueries({ queryKey: ["capture-attempts"] });
                  await leadsQuery.refetch();
                  toast.success("Lista atualizada");
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "Falha ao atualizar");
                }
              }}
            >
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${leadsQuery.isFetching ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Atualizar</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              onClick={exportCSV}
              title="Planilha com uma coluna por parâmetro de UTM (source, medium, campaign, content, term) e a UTM completa. As colunas utm_content e utm_term só vêm preenchidas quando o anúncio envia esses parâmetros."
            >
              <Download className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">CSV</span><span className="sm:hidden">CSV</span>
            </Button>
            <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={handleLogout}>
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </header>

        <div className="flex flex-col gap-3 mb-4">
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <Input
              placeholder="Buscar por nome, email ou telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:max-w-sm"
            />
            <PeriodFilter
              preset={period.preset}
              setPreset={period.setPreset}
              customFrom={period.customFrom}
              setCustomFrom={period.setCustomFrom}
              customTo={period.customTo}
              setCustomTo={period.setCustomTo}
            />
          </div>
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="Filtrar tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {(Object.keys(TIPO_LABELS) as LeadTipo[]).map((k) => (
                  <SelectItem key={k} value={k}>{TIPO_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={originFilter} onValueChange={setOriginFilter}>
              <SelectTrigger className="w-full sm:w-[240px]">
                <SelectValue placeholder="Filtrar LP / origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as LPs</SelectItem>
                {originOptions.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={posFilter} onValueChange={setPosFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="Filtrar pós" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as pós</SelectItem>
                {posOptions.map((p) => (
                  <SelectItem key={p} value={p}>{posLabel(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={tempFilter} onValueChange={setTempFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filtrar temperatura" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as temperaturas</SelectItem>
                <SelectItem value="quente">🔥 Quente</SelectItem>
                <SelectItem value="morno">🌤️ Morno</SelectItem>
                <SelectItem value="frio">❄️ Frio</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sellerFilter} onValueChange={setSellerFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filtrar vendedora" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as vendedoras</SelectItem>
                {SELLERS.map((s) => (
                  <SelectItem key={s.email} value={s.email}>👩‍💼 {s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {perOrigin.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {perOrigin.map((r) => (
              <Badge key={r.origem} variant="secondary" className="text-xs">
                {r.origem}: <span className="ml-1 font-semibold">{r.total}</span>
              </Badge>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-4 mb-6 items-stretch">

          {/* Coluna esquerda: KPIs empilhados */}
          <div className="lg:col-start-1 lg:row-start-1 grid grid-cols-2 lg:grid-cols-1 gap-2 order-first lg:order-none h-full">

            <div className="rounded-lg border bg-card px-3 py-2 flex flex-col justify-center">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Total leads</div>
              <div className="text-xl font-bold mt-0.5">{kpis.total}</div>
            </div>
            <div className="rounded-lg border bg-card px-3 py-2 flex flex-col justify-center">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Parciais (abandono)</div>
              <div className="text-xl font-bold mt-0.5">{kpis.parcial}</div>
            </div>
            <div className="rounded-lg border bg-card px-3 py-2 flex flex-col justify-center">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Frio</div>
              <div className="text-xl font-bold mt-0.5 text-sky-400">{kpis.frio}</div>
            </div>
            <div className="rounded-lg border bg-card px-3 py-2 flex flex-col justify-center">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Morno</div>
              <div className="text-xl font-bold mt-0.5 text-yellow-300">{kpis.morno}</div>
            </div>
            <div className="rounded-lg border bg-card px-3 py-2 flex flex-col justify-center">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Quente</div>
              <div className="text-xl font-bold mt-0.5 text-orange-400">{kpis.quente}</div>
            </div>
            <div className="rounded-lg border bg-card px-3 py-2 flex flex-col justify-center">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Conversões</div>
              <div className="text-xl font-bold mt-0.5 text-primary">{conversions.total}</div>
              {conversions.total > 0 && (
                <div className="text-[10px] font-semibold mt-0.5 text-muted-foreground">
                  <span>{conversions.crm} CRM</span>
                  <span className="opacity-50 mx-1">·</span>
                  <span>{conversions.mkt} MKT</span>
                </div>
              )}
            </div>
          </div>



          {/* Coluna direita: funil de conversão (barras horizontais) */}
          <div className="lg:col-start-2 lg:row-start-1 h-full">
        {(() => {
          const s = funnel.sessions;
          const st = funnel.started;
          const c = funnel.captured;
          let capturedQuente = 0, capturedMorno = 0, capturedFrio = 0;
          const emailsCaptured = new Set<string>();
          for (const l of filtered) {
            if (l.respostas && l.respostas.length > 0) {
              const cl = (effectiveClassificacao(l) ?? "").toLowerCase();
              if (cl === "quente") capturedQuente += 1;
              else if (cl === "morno") capturedMorno += 1;
              else if (cl === "frio") capturedFrio += 1;
              const em = (l.email ?? "").trim().toLowerCase();
              if (em) emailsCaptured.add(em);
            }
          }
          let convTotal = 0, convCrm = 0, convMkt = 0;
          for (const em of emailsCaptured) {
            const ps = purchasesByEmail[em] ?? [];
            if (ps.length === 0) continue;
            convTotal += 1;
            if (ps.some((p) => p.channel === "CRM")) convCrm += 1;
            else convMkt += 1;
          }
          const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);

          // Container neutro (tema); cores ficam apenas no texto, número e barra
          const stages = [
            { key: "sessions", label: "SESSÕES", value: s, convFromPrev: null as number | null, note: "visitantes únicos",
              text: "text-sky-300" },
            { key: "started", label: "INICIARAM CAPTURA", value: st, convFromPrev: pct(st, s), note: "deixaram contato",
              text: "text-cyan-200" },
            { key: "captured", label: "LEADS CAPTURADOS", value: c, convFromPrev: pct(c, st), note: "qualificação completa",
              text: "text-yellow-200" },
            { key: "quentes", label: "LEADS QUENTES", value: capturedQuente, convFromPrev: pct(capturedQuente, c), note: "alta intenção de compra",
              text: "text-orange-300" },
            { key: "conversoes", label: "CONVERSÕES", value: convTotal, convFromPrev: pct(convTotal, capturedQuente), note: "compras confirmadas",
              text: "text-primary" },
          ];
          const maxVal = Math.max(1, ...stages.map((st) => st.value));

          return (
            <div className="flex flex-col gap-2 h-full">
              {stages.map((stage) => {
                const barPct = stage.value === 0 ? 0 : (stage.value / maxVal) * 100;
                const pctTotal = pct(stage.value, s);
                return (
                  <div
                    key={stage.key}
                    className="flex-1 min-h-[84px] rounded-md border border-border bg-card/60 flex items-stretch overflow-hidden"
                  >

                    {/* Área fixa: label + número (mesma para todas as etapas) */}
                    <div className="w-[300px] shrink-0 flex items-center gap-4 px-4 py-2">
                      <div className="min-w-0 flex-1 flex flex-col justify-center leading-tight">
                        <div className={`text-[13px] font-bold uppercase tracking-wider ${stage.text}`}>
                          {stage.label}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {stage.key === "sessions"
                            ? stage.note
                            : `${stage.convFromPrev}% da etapa anterior · ${pctTotal}% do total`}
                        </div>
                        {stage.key === "quentes" && c > 0 && (
                          <div className="text-[11px] font-semibold mt-0.5 text-muted-foreground">
                            <span>{capturedMorno} morno</span>
                            <span className="opacity-50 mx-1">·</span>
                            <span>{capturedFrio} frio</span>
                          </div>
                        )}
                        {stage.key === "conversoes" && convTotal > 0 && (
                          <div className="text-[11px] font-semibold mt-0.5 text-primary/80">
                            <span>{convCrm} CRM</span>
                            <span className="opacity-50 mx-1">·</span>
                            <span>{convMkt} MKT</span>
                          </div>
                        )}
                      </div>
                      <div className={`text-3xl font-extrabold tabular-nums shrink-0 ${stage.text}`}>
                        {stage.value.toLocaleString("pt-BR")}
                      </div>
                    </div>
                    {/* Trilho proporcional — cresce de 0 até o máximo do funil */}
                    <div className="flex-1 relative flex items-center px-2">
                      <div
                        style={{ width: `${barPct}%` }}
                        className={`h-3 rounded-full transition-[width] duration-500 ${
                          stage.key === "sessions" ? "bg-sky-400/70" :
                          stage.key === "started" ? "bg-cyan-300/70" :
                          stage.key === "captured" ? "bg-yellow-300/70" :
                          stage.key === "quentes" ? "bg-orange-400/80" :
                          "bg-primary/80"
                        }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );


        })()}
          </div>
        </div>








        {leadsQuery.isLoading ? (
          <div className="p-10 text-center text-muted-foreground">Carregando leads...</div>
        ) : leadsQuery.error ? (
          <div className="p-10 text-center text-destructive">
            Erro: {(leadsQuery.error as Error).message}
          </div>
        ) : (
          <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3 text-sm">
            <div className="text-muted-foreground">
              {sorted.length === 0 ? (
                <>Nenhum lead no filtro atual.</>
              ) : (
                <>
                  Mostrando <span className="font-medium text-foreground">{pageStart + 1}–{pageEnd}</span>{" "}
                  de <span className="font-medium text-foreground">{sorted.length}</span> leads
                  {sorted.length !== leads.length && (
                    <span className="text-muted-foreground/70"> (de {leads.length} totais)</span>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground whitespace-nowrap">Por página</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="h-8 w-[84px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[30, 50, 100, 200, 500].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Anterior</Button>
                <span className="text-muted-foreground px-2 whitespace-nowrap">{currentPage + 1} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>Próxima</Button>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8 p-1"></TableHead>
                  <TableHead onClick={() => toggleSort("created_at")} className="cursor-pointer select-none hover:text-foreground whitespace-nowrap w-[120px]">Data{sortIndicator("created_at")}</TableHead>
                  <TableHead onClick={() => toggleSort("nome")} className="cursor-pointer select-none hover:text-foreground whitespace-nowrap w-[20%]">Lead{sortIndicator("nome")}</TableHead>
                  <TableHead onClick={() => toggleSort("origem")} className="cursor-pointer select-none hover:text-foreground whitespace-nowrap w-[14%]">Origem{sortIndicator("origem")}</TableHead>
                  <TableHead className="whitespace-nowrap w-[14%]" title="Lead enviado ao RD Station Marketing com as tags do fluxo.">RD Station</TableHead>
                  <TableHead className="whitespace-nowrap w-[15%]" title="Negociação criada no RD CRM: funil de destino e vendedora responsável.">RD CRM</TableHead>
                  <TableHead className="whitespace-nowrap w-[15%]" title="Compras confirmadas na Eduzz (pagas). Formato: PRODUTO - FONTE (CRM = venda pelo WhatsApp; MKT = venda por LP/ads/outra fonte digital).">Compras</TableHead>
                  <TableHead className="text-right whitespace-nowrap w-[64px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                      Nenhum lead encontrado.
                    </TableCell>
                  </TableRow>
                )}
                {paged.map((l) => {
                   const isOpen = expanded.has(l.id);
                   const respostas = l.respostas ?? [];
                   const history = historyByEmail.get((l.email ?? "").trim().toLowerCase()) ?? [];
                   const isPartial = respostas.length === 0;
                   return (
                   <Fragment key={l.id}>
                   <TableRow key={l.id}>
                     <TableCell className="p-1 align-middle">
                       <div className="flex flex-col items-center gap-0.5">
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-7 w-7"
                           onClick={() => toggleExpand(l.id)}
                           aria-label={isOpen ? "Recolher detalhes" : "Ver detalhes"}
                           title={respostas.length > 0 ? `Ver ${respostas.length} resposta(s) + histórico` : "Ver histórico do lead"}
                         >
                           {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                         </Button>
                         {isPartial && (
                           <span
                             className="inline-flex h-4 items-center rounded-md border border-amber-500/30 bg-amber-500/10 px-1 text-[9px] font-medium text-amber-500"
                             title="Lead parcial — contato capturado, qualificação ainda não concluída"
                           >
                             parcial
                           </span>
                         )}
                       </div>
                     </TableCell>
                    <TableCell className="text-xs whitespace-nowrap align-middle">
                      {formatBR(l.created_at)}
                    </TableCell>
                    <TableCell className="min-w-0 align-top">
                      <div className="space-y-0.5 min-w-0">
                        <div className="font-medium text-sm truncate" title={l.nome}>{l.nome}</div>
                        <div className="text-xs text-muted-foreground truncate" title={l.email}>{l.email}</div>
                        <div className="text-xs text-muted-foreground truncate" title={l.telefone}>{l.telefone}</div>
                        {(() => {
                          const cls = (effectiveClassificacao(l) ?? "").toLowerCase();
                          if (!cls || (cls !== "quente" && cls !== "morno" && cls !== "frio")) return null;
                          const b = classifBadge(cls);
                          const pf = perfilLead(l);
                          return (
                            <div className="flex flex-wrap gap-1 pt-0.5">
                              <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${b.cls}`}>{b.label}</span>
                              {pf && <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${pf === "FORMADO" ? "border-cyan-400/30 bg-cyan-400/10 text-cyan-300" : "border-amber-400/30 bg-amber-400/10 text-amber-300"}`}>{pf}</span>}
                            </div>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs min-w-0 align-top">
                      {(() => {
                        const k = origemKind(l.origem, l.area);
                        const url = urlForOrigem(l.origem, l.area);
                        return (
                          <div className="space-y-1 min-w-0">
                            <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${k.cls}`}>{k.label}</span>
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="block font-mono text-[10px] text-muted-foreground hover:underline truncate"
                              title={url}
                            >
                              {url}
                            </a>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-xs min-w-0 align-top">
                      <div className="space-y-1 min-w-0">
                        {l.delivered_to_rdstation ? (
                          <span className="inline-flex items-center rounded border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-400">enviado</span>
                        ) : l.rdstation_error === DISCARDED_REASON ? (
                          <span className="inline-flex items-center rounded border border-slate-500/30 bg-slate-500/10 px-2 py-0.5 text-[11px] font-medium text-white/70" title={l.rdstation_error ?? undefined}>DESCARTADO</span>
                        ) : (
                          <div className="flex flex-col items-start gap-1">
                            <Badge variant="destructive" title={l.rdstation_error ?? undefined}>falhou</Badge>
                            <RetryButton leadId={l.id} target="rdstation" />
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1 pt-0.5">
                          {[
                            ...rdTagsFor(l.origem, l.area, effectiveClassificacao(l)),
                            ...(l.crm_error === DISCARDED_REASON ? ["EXCLUIR"] : []),
                          ].map((t) => (
                            <code
                              key={t}
                              className="inline-flex items-center rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground truncate max-w-full"
                            >
                              {t}
                            </code>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs min-w-0 align-top">
                      {(() => {
                        const err = l.crm_error ?? "";
                        if (!l.delivered_to_crm) {
                          if (err.startsWith("pending:")) {
                            return (
                              <span className="inline-flex items-center rounded border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-admin-accent" title={err}>
                                ⏳ aguardando 10min
                              </span>
                            );
                          }
                          if (err.startsWith("skipped:")) {
                            return (
                              <span className="inline-flex items-center rounded border border-slate-500/30 bg-slate-500/10 px-2 py-0.5 text-[11px] font-medium text-white/70" title={err}>
                                🛒 Comprou direto (MKT)
                              </span>
                            );
                          }
                          if (err === DISCARDED_REASON) {
                            return (
                              <span className="inline-flex items-center rounded border border-slate-500/30 bg-slate-500/10 px-2 py-0.5 text-[11px] font-medium text-white/70" title={err}>DESCARTADO</span>
                            );
                          }
                          if (err.startsWith("não enviado ao CRM")) {
                            return (
                              <span className="inline-flex items-center rounded border border-slate-500/30 bg-slate-500/10 px-2 py-0.5 text-[11px] font-medium text-white/70" title={err}>NÃO ENVIADO</span>
                            );
                          }

                          if (err) {
                            return (
                              <div className="flex flex-col items-start gap-1">
                                <Badge variant="destructive" title={err}>falhou</Badge>
                                <RetryButton leadId={l.id} target="crm" />
                              </div>
                            );
                          }
                          return <span className="text-muted-foreground">—</span>;
                        }
                        return (
                          <div className="flex flex-col items-start gap-1 min-w-0">
                            {err.startsWith("won:") ? (
                              <span className="inline-flex items-center rounded border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300" title={err}>
                                🏆 Ganho (MKT)
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-400">enviado</span>
                            )}
                            {(() => {
                              const f = funilLabel(l.crm_stage_id);
                              return f ? (
                                <code className="inline-flex w-fit items-center rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground truncate max-w-full">
                                  {f.label}
                                </code>
                              ) : null;
                            })()}
                            {(() => {
                              const sellerEmail = l.crm_seller_email?.toLowerCase() ?? "";
                              const blackSeller = (l.origem === "black26-lista" || l.origem === "black26-lista-entregas" || l.origem === "black26-lista-quizz")
                                ? BLACK_SELLERS.find((s) => s.email.toLowerCase() === sellerEmail)
                                : undefined;
                              const seller = blackSeller
                                ? { nome: `${blackSeller.nome} · ${blackSeller.crm === "rd" ? "RD CRM" : "FEO CRM"}`, email: blackSeller.email }
                                : sellerEmail
                                  ? SELLERS.find((s) => s.email.toLowerCase() === sellerEmail) ?? null
                                  : null;
                              return seller ? (
                                <span
                                  className="inline-flex w-fit items-center rounded-md border border-indigo-500/30 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-medium text-indigo-300"
                                  title={`Vendedora: ${seller.email}`}
                                >
                                  👩‍💼 {seller.nome}
                                </span>
                              ) : (
                                <span className="text-[10px] text-muted-foreground/60" title="Vendedora não atribuída (lead antigo, anterior à correção do fluxo).">—</span>
                              );
                            })()}
                            {l.crm_deal_id && (
                              <code className="hidden font-mono text-[10px] text-muted-foreground truncate" title={l.crm_deal_id}>
                                {l.crm_deal_id}
                              </code>
                            )}
                            {err && !err.startsWith("won:") && !err.startsWith("skipped:") && !err.startsWith("pending:") && (
                              <div className="text-[10px] text-admin-accent truncate" title={err}>⚠️ nota falhou</div>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>

                    <TableCell className="text-xs min-w-0 align-top">
                      {(() => {
                        // Compras ATRIBUÍDAS a esta conversão (janela de atribuição),
                        // não todas as compras do e-mail. Fallback pro modo antigo
                        // enquanto a inteligência ainda não foi recalculada.
                        // Histórico completo da pessoa: compras por e-mail OU telefone.
                        const byEm = purchasesByEmail[(l.email ?? "").trim().toLowerCase()] ?? [];
                        const ph = (l.telefone ?? "").replace(/\D/g, "").slice(-11);
                        const byPh = ph.length >= 10 ? (purchasesByPhone[ph] ?? []) : [];
                        const allPaid = [...byEm, ...byPh.filter((p) => !byEm.some((e) => e.tag === p.tag && e.channel === p.channel))]
                          .sort((a, b) => (a.paid_at ?? "").localeCompare(b.paid_at ?? ""));
                        const purchases = allPaid;
                        const others = 0;
                        void attributedByLeadId; void attributedTotalByLeadId;
                        const hist = (histByEmail[(l.email ?? "").trim().toLowerCase()] ?? []).filter(
                          (t) => !allPaid.some((p) => p.tag === t),
                        );
                        const histBadges = hist.map((t) => (
                          <span key={`hist-${t}`} className="inline-flex items-center gap-1" title="Aluno de turma anterior (lista de alunos até 10/09/2026)">
                            <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${purchaseTagCls(t)}`}>
                              {purchaseProductLabel(t)}
                            </span>
                            <span className="inline-flex items-center rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                              até 10/09/26
                            </span>
                          </span>
                        ));
                        if (purchases.length === 0 && hist.length > 0) {
                          return <div className="flex flex-wrap gap-1">{histBadges}</div>;
                        }
                        if (purchases.length === 0) {
                          return others > 0 ? (
                            <span className="text-[10px] text-white/45" title="Compras da pessoa creditadas a outra conversão">
                              +{others} em outra conversão
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[11px]">—</span>
                          );
                        }
                        return (
                          <div className="flex flex-wrap gap-1">
                            {purchases.map((p, pi) => {
                              const label = purchaseProductLabel(p.tag);
                              const title = `${p.product_name ?? ""}${p.paid_at ? ` · ${formatBR(p.paid_at)}` : ""}`;
                              return (
                                <span key={`${p.tag}|${p.channel}|${pi}`} className="inline-flex items-center gap-1" title={title}>
                                  <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${purchaseTagCls(p.tag)}`}>
                                    {label}
                                  </span>
                                  <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${CHANNEL_CLS[p.channel]}`}>
                                    {p.channel}
                                  </span>
                                  {p.paid_at ? <span className="text-[10px] text-muted-foreground">{formatBR(p.paid_at).slice(0, 10)}</span> : null}
                                </span>
                              );
                            })}
                            {histBadges}
                            {others > 0 && (
                              <span className="text-[10px] text-white/45" title="Outras compras da pessoa creditadas a outra conversão">
                                +{others}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right p-1 align-top">
                      <div className="flex justify-end gap-1">
                        <Button
                          asChild
                          variant="ghost"
                          size="icon"
                          aria-label="Abrir WhatsApp"
                          title="Falar no WhatsApp"
                        >
                          <a href={waLink(l.telefone, l.nome)} target="_blank" rel="noopener noreferrer">
                            <MessageCircle className="h-4 w-4 text-green-600" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(l.id)}
                          aria-label="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                     <TableRow key={`${l.id}-resp`} className="bg-muted/30 hover:bg-muted/30">
                       <TableCell></TableCell>
                       <TableCell colSpan={7} className="py-3">
                         <div className="space-y-4">
                           {respostas.length > 0 && (
                             <div className="space-y-2">
                               <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                 Respostas da captura ({respostas.length})
                               </div>
                               <ul className="space-y-1.5 text-sm">
                                 {respostas.map((r, i) => (
                                   <li key={i} className="flex gap-2">
                                     <span className="text-muted-foreground shrink-0">{i + 1}.</span>
                                     <div>
                                       <div className="font-medium">{r.pergunta}</div>
                                       <div className="text-foreground/80">→ {r.resposta}</div>
                                     </div>
                                   </li>
                                 ))}
                               </ul>
                               <div className="text-[11px] text-muted-foreground">
                                 No RD Station essas respostas aparecem no campo personalizado <code className="font-mono">cf_respostas_qualificacao</code>.
                               </div>
                             </div>
                           )}
                           {(() => {
                             const utms: Array<[string, string | null]> = [
                               ["utm_source", l.utm_source],
                               ["utm_medium", l.utm_medium ?? null],
                               ["utm_campaign", l.utm_campaign ?? null],
                               ["utm_content", l.utm_content ?? null],
                               ["utm_term", l.utm_term ?? null],
                             ];
                             const preenchidas = utms.filter(([, v]) => v && v.trim() !== "");
                             const qs = preenchidas.map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join("&");
                             const fullUrl = l.landing_path
                               ? `${l.landing_path}${qs ? (l.landing_path.includes("?") ? "&" : "?") + qs : ""}`
                               : qs;
                             return (
                               <div className="space-y-2 pt-3 border-t border-border/60">
                                 <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                   Rastreamento da conversão
                                 </div>
                                 <div className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
                                   {[
                                     ["Origem (interna)", l.origem],
                                     ["Página de captura", l.landing_path ?? null],
                                     ["Dispositivo", l.device ?? null],
                                     ["Sessão", l.session_id ?? null],
                                     ...utms.map(([k, v]) => [k, v] as [string, string | null]),
                                   ].map(([label, value]) => (
                                     <div key={label as string} className="flex gap-2 min-w-0">
                                       <span className="text-muted-foreground shrink-0">{label}:</span>
                                       {value ? (
                                         <code className="font-mono text-[11px] text-foreground/90 truncate" title={String(value)}>
                                           {value}
                                         </code>
                                       ) : (
                                         <span className="text-muted-foreground/60">—</span>
                                       )}
                                     </div>
                                   ))}
                                 </div>
                                 <div className="space-y-1">
                                   <div className="text-[11px] text-muted-foreground">UTM completa da conversão</div>
                                   {fullUrl ? (
                                     <code className="block break-all rounded border border-border bg-muted/40 px-2 py-1.5 font-mono text-[11px] text-foreground/90">
                                       {fullUrl}
                                     </code>
                                   ) : (
                                     <div className="text-[11px] text-muted-foreground/70">
                                       Sem UTMs registradas (tráfego direto ou link sem parâmetros).
                                     </div>
                                   )}
                                 </div>
                               </div>
                             );
                           })()}
                           <div className="space-y-2 pt-3 border-t border-border/60">

                             <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                               Histórico completo do lead ({history.length})
                             </div>
                             {history.length === 0 ? (
                               <div className="text-xs text-muted-foreground">Sem eventos registrados para este e-mail.</div>
                             ) : (
                               <ul className="space-y-1.5 text-sm">
                                 {history.map((ev, i) => (
                                   <li key={i} className="flex flex-wrap gap-2 items-center">
                                     <span className="font-mono text-[11px] text-muted-foreground shrink-0 min-w-[110px]">
                                       {ev.date ? formatBR(ev.date) : "—"}
                                     </span>
                                      {ev.kind === "capture" ? (
                                        <>
                                          <span className="text-xs text-foreground/90">converteu em</span>
                                          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${ev.lpCls}`}>
                                            {ev.lpLabel}
                                          </span>
                                          <span className="text-xs text-muted-foreground">→</span>
                                          <div className="flex flex-wrap gap-1">
                                            {ev.tags.length === 0 ? (
                                              <span className="text-[11px] text-muted-foreground">sem tags</span>
                                            ) : ev.tags.map((t) => (
                                              <code key={t} className="inline-flex items-center rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                                                {t}
                                              </code>
                                            ))}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <span className="text-xs text-foreground/90">comprou</span>
                                          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${ev.productCls}`}>
                                            {ev.productLabel}
                                          </span>
                                          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${CHANNEL_CLS[ev.channel]}`}>
                                            {ev.channel}
                                          </span>
                                        </>
                                      )}
                                   </li>
                                 ))}
                               </ul>
                             )}
                           </div>
                         </div>
                       </TableCell>
                     </TableRow>
                   )}
                  </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          </>
        )}

        <div className="grid gap-6 lg:grid-cols-2 mt-8">
          <div className="rounded-lg border bg-card p-5">
            <h2 className="font-semibold mb-1">Leads por dia</h2>
            <p className="text-xs text-muted-foreground mb-4">Distribuição diária por classificação.</p>
            <div className="h-64">
              {perDay.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados no período.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={perDay} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="Frio" stroke="#38bdf8" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Morno" stroke="#fde047" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Quente" stroke="#fb923c" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Total" stroke="#6b7280" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-5">
            <h2 className="font-semibold mb-1">Leads por LP / origem</h2>
            <p className="text-xs text-muted-foreground mb-4">Top 8 origens no período filtrado.</p>
            <div className="h-64">
              {perOriginChart.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sem dados no período.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={perOriginChart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="origem" fontSize={10} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Frio" stackId="cls" fill="#38bdf8" />
                    <Bar dataKey="Morno" stackId="cls" fill="#fde047" />
                    <Bar dataKey="Quente" stackId="cls" fill="#fb923c" radius={[4, 4, 0, 0]} />

                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {perOriginFunnel.length > 1 && (
          <div className="mt-6 rounded-lg border bg-card p-5">
            <h2 className="font-semibold mb-1">Funil por LP / origem</h2>
            <p className="text-xs text-muted-foreground mb-3">Sessões → iniciaram captura → leads capturados por origem.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-muted/40">
                  <tr className="text-left">
                    <th className="p-2 border">Origem</th>
                    <th className="p-2 border text-right">Sessões</th>
                    <th className="p-2 border text-right">Iniciaram captura</th>

                    <th className="p-2 border text-right">Leads capturados</th>
                    <th className="p-2 border text-right">Conversão total</th>
                  </tr>
                </thead>
                <tbody>
                  {perOriginFunnel.map((r) => (
                    <tr key={r.origem}>
                      <td className="p-2 border font-mono text-[11px]">{r.origem}</td>
                      <td className="p-2 border text-right">{r.sessions.toLocaleString("pt-BR")}</td>
                      <td className="p-2 border text-right">{r.started.toLocaleString("pt-BR")}</td>
                      <td className="p-2 border text-right font-semibold">{r.captured.toLocaleString("pt-BR")}</td>
                      <td className="p-2 border text-right text-muted-foreground">
                        {r.sessions > 0 ? `${Math.round((r.captured / r.sessions) * 1000) / 10}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        



        <div className="mt-8 rounded-xl border border-border/60 bg-gradient-to-b from-card to-card/60 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4 pb-4 border-b border-border/50">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight">Capturas, tags e fluxo do lead — referência completa</h2>
              <p className="text-xs text-muted-foreground mt-1 max-w-3xl leading-relaxed">
                Documentação viva do pipeline unificado server-side (
                <code className="rounded bg-muted px-1 py-0.5 text-[10px]">src/lib/leads/postprocess.server.ts</code>) usado por todas as LPs, pop-ups, quizzes e
                capturas futuras. Inclui validação Zod, tags RD, roteamento RD CRM, evento no dataLayer para
                o GTM e auditoria. <b className="text-foreground">Meta Pixel + CAPI ficam 100% no GTM</b> — o site não envia mais nada
                direto pro Meta.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <a
                href="/fluxo-completo-captura-qualificacao-feo.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors"
              >
                ⬇ PDF — Fluxo completo
              </a>
              <Link
                to="/admin/plano-leads-rd-crm"
                className="inline-flex items-center rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-[11px] font-semibold hover:bg-muted transition-colors"
              >
                Ver projeto RD/CRM →
              </Link>
            </div>
          </div>

          {/* 0) Qualificação FRIO / MORNO / QUENTE / DESCARTADO */}
          <section className="mt-2">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
              <div>
                <h3 className="text-base font-semibold tracking-tight text-foreground">
                  Fluxo de qualificação
                </h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Cada lead cai em uma das 4 classificações abaixo. A regra vive em{" "}
                  <code className="rounded bg-muted px-1 py-0.5 text-[10px]">src/lib/leads/qualification.ts</code>{" "}
                  (<code className="text-[10px]">qualifyByAnswers</code>) — aplicada nos quizzes, ementas, aulas
                  experimentais, revista e imersão PBE.
                </p>
              </div>
              <a
                href="/qualificacao-leads-feo.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10 transition-colors"
              >
                ⬇ PDF completo
              </a>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {/* FRIO */}
              <div className="relative overflow-hidden rounded-lg border border-sky-400/40 bg-sky-500/5">
                <div className="h-1 w-full bg-sky-400" />
                <div className="p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg leading-none">❄️</span>
                    <span className="text-[13px] font-bold text-sky-300 tracking-wide">FRIO</span>
                  </div>
                  <div className="text-[11.5px] text-muted-foreground leading-relaxed space-y-1.5">
                    <p>
                      Fisioterapeuta que respondeu{" "}
                      <b className="text-foreground">"Não pretendo iniciar uma pós"</b>.
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-sky-400/20 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      ✓ RD Marketing
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-500/15 text-white/70 border border-slate-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      — Sem CRM
                    </span>
                  </div>
                </div>
              </div>

              {/* MORNO */}
              <div className="relative overflow-hidden rounded-lg border border-yellow-400/40 bg-yellow-500/5">
                <div className="h-1 w-full bg-yellow-400" />
                <div className="p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg leading-none">🌤️</span>
                    <span className="text-[13px] font-bold text-yellow-300 tracking-wide">MORNO</span>
                  </div>
                  <div className="text-[11.5px] text-muted-foreground leading-relaxed space-y-1.5">
                    <p>
                      <b className="text-foreground">Fisioterapeuta:</b> "Sim, entre 6 e 12 meses" ou "Ainda
                      estou avaliando".
                    </p>
                    <p>
                      <b className="text-foreground">Estudante</b> (5º ano ou formando em 6m) que pretende iniciar{" "}
                      <b className="text-foreground">imediatamente</b> ou{" "}
                      <b className="text-foreground">nos primeiros 6 meses</b>.
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-yellow-400/20 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      ✓ RD Marketing
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      → CRM: FUNIL-POS
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      → CRM: UNIVERSITÁRIOS
                    </span>
                  </div>
                </div>
              </div>

              {/* QUENTE */}
              <div className="relative overflow-hidden rounded-lg border border-orange-400/40 bg-orange-500/5">
                <div className="h-1 w-full bg-orange-400" />
                <div className="p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg leading-none">🔥</span>
                    <span className="text-[13px] font-bold text-orange-300 tracking-wide">QUENTE</span>
                  </div>
                  <div className="text-[11.5px] text-muted-foreground leading-relaxed space-y-1.5">
                    <p>
                      Fisioterapeuta (formado ou com pós) que respondeu{" "}
                      <b className="text-foreground">"Sim, próximos 3 meses"</b> ou{" "}
                      <b className="text-foreground">"Sim, entre 3 e 6 meses"</b>.
                    </p>
                    <p>
                      Também <b className="text-foreground">todo pré-checkout</b> (intenção de compra direta).
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-orange-400/20 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      ✓ RD Marketing
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      → CRM: FUNIL-POS
                    </span>
                  </div>
                </div>
              </div>

              {/* DESCARTADO */}
              <div className="relative overflow-hidden rounded-lg border border-slate-500/40 bg-slate-500/5">
                <div className="h-1 w-full bg-slate-500" />
                <div className="p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg leading-none">🚫</span>
                    <span className="text-[13px] font-bold text-white/70 tracking-wide">DESCARTADO</span>
                  </div>
                  <div className="text-[11.5px] text-muted-foreground leading-relaxed space-y-1.5">
                    <p><b className="text-foreground">Outro profissional</b> da saúde (não é fisio).</p>
                    <p>Estudante do <b className="text-foreground">1º ao 4º ano</b>.</p>
                    <p>
                      Estudante formando+6m que quer esperar <b className="text-foreground">6-12 meses</b>,{" "}
                      <b className="text-foreground">ganhar experiência</b> antes ou{" "}
                      <b className="text-foreground">não pretende fazer pós</b>.
                    </p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-500/20 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      ✓ RD Marketing
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      + tag EXCLUIR
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-500/15 text-white/70 border border-slate-500/30 px-2 py-0.5 text-[10px] font-semibold">
                      — Sem CRM
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Regras diretas — sem quiz */}
            <div className="mt-3 rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Regras diretas (sem quiz)
              </div>
              <div className="grid gap-2 sm:grid-cols-3 text-[11.5px]">
                <div className="rounded-md bg-background/60 border border-border/50 p-2.5">
                  <div className="font-semibold text-foreground mb-0.5">Pré-checkout</div>
                  <div className="text-muted-foreground">
                    Padrão + GG. Sempre <b className="text-orange-300">QUENTE</b> → FUNIL-POS.
                  </div>
                </div>
                <div className="rounded-md bg-background/60 border border-border/50 p-2.5">
                  <div className="font-semibold text-foreground mb-0.5">AprovaFEO / Bolsa / Plantão</div>
                  <div className="text-muted-foreground">
                    Captação direta — não passa por qualifyByAnswers. Herda regra própria.
                  </div>
                </div>
                <div className="rounded-md bg-background/60 border border-border/50 p-2.5">
                  <div className="font-semibold text-foreground mb-0.5">Tag EXCLUIR no RD</div>
                  <div className="text-muted-foreground">
                    Anexada em DESCARTADOS para o time limpar/desconsiderar periodicamente.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Divisor visual antes das seções detalhadas */}
          <div className="mt-8 mb-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-border/60" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">
              Referência técnica completa
            </span>
            <div className="h-px flex-1 bg-border/60" />
          </div>

          <Accordion type="multiple" defaultValue={[]} className="space-y-2">
            <AccordionItem value="fontes" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                1. Fontes de captura ativas ({CAPTURE_FLOWS.length})
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 border">Fluxo</th>
                        <th className="p-2 border">Formato</th>
                        <th className="p-2 border">Origem (campo <code>origem</code>)</th>
                        <th className="p-2 border">Campos obrigatórios</th>
                        <th className="p-2 border">Qualificação</th>
                        <th className="p-2 border">Parcial?</th>
                        <th className="p-2 border">dataLayer (GTM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CAPTURE_FLOWS.map((flow) => {
                        const t = tagsForFlow(flow);
                        return (
                          <tr key={flow.source}>
                            <td className="p-2 border font-medium">{flow.label}</td>
                            <td className="p-2 border text-muted-foreground">{flow.formato}</td>
                            <td className="p-2 border font-mono text-[11px]">
                              {t.origem}
                              {flow.slugs.length > 0 && <span className="text-muted-foreground"> · slug = {flow.slugLabel}</span>}
                            </td>
                            <td className="p-2 border">{flow.camposObrigatorios}</td>
                            <td className="p-2 border">{flow.qualificacao}</td>
                            <td className="p-2 border text-center">{flow.temParcial ? "✅" : "—"}</td>
                            <td className="p-2 border text-center">{flow.meta ? "✅" : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  <b>Regra dura:</b> nenhuma captura é aceita sem nome, e-mail e WhatsApp válidos. Schema Zod em{" "}
                  <code>src/lib/leads/validation.ts</code> roda no cliente e no servidor. Catálogo em{" "}
                  <code>src/lib/leads/capture-flow-catalog.ts</code>.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="lps" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                2. LPs / rotas onde cada captura roda
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 border">Fluxo</th>
                        <th className="p-2 border">Rotas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CAPTURE_FLOWS.map((flow) => (
                        <tr key={flow.source}>
                          <td className="p-2 border font-medium whitespace-nowrap">{flow.label}</td>
                          <td className="p-2 border">
                            <div className="flex flex-wrap gap-1.5">
                              {flow.lps.map((lp) => (
                                <a
                                  key={lp}
                                  href={lp.includes("{slug}") ? undefined : lp}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={
                                    lp.includes("{slug}")
                                      ? "font-mono text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                                      : "font-mono text-[11px] px-1.5 py-0.5 rounded bg-muted hover:bg-muted/70 text-blue-600 hover:underline"
                                  }
                                >
                                  {lp}
                                </a>
                              ))}
                            </div>
                            {flow.observacoes && (
                              <p className="text-[11px] text-muted-foreground mt-1.5">{flow.observacoes}</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="pos" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                3. Mapeamento por pós ({POS_LPS.length})
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 border">Pós</th>
                        <th className="p-2 border">Slug</th>
                        <th className="p-2 border">Venda (padrão)</th>
                        <th className="p-2 border">Venda (GG)</th>
                        <th className="p-2 border">Aula exp.</th>
                        <th className="p-2 border">Origem ementa</th>
                        <th className="p-2 border">Origem ementa LP (D)</th>
                        <th className="p-2 border">Origem pré-checkout</th>
                        <th className="p-2 border">Origem pré-checkout GG</th>
                      </tr>
                    </thead>
                    <tbody>
                      {POS_LPS.map((r) => (
                        <tr key={r.code}>
                          <td className="p-2 border">
                            <div className="font-mono font-semibold">{r.code}</div>
                            <div className="text-[11px] text-muted-foreground">{r.posNome}</div>
                          </td>
                          <td className="p-2 border font-mono text-[11px]">{r.slug}</td>
                          <td className="p-2 border">
                            <a href={r.vendaPadrao} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                              {r.vendaPadrao}
                            </a>
                          </td>
                          <td className="p-2 border">
                            {r.vendaGg ? (
                              <a href={r.vendaGg} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                {r.vendaGg}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-2 border">
                            {r.aulaExperimental ? (
                              <a href={r.aulaExperimental} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                {r.aulaExperimental}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-2 border font-mono text-[11px]">{r.ementa}</td>
                          <td className="p-2 border font-mono text-[11px]">{r.ementaLp}</td>
                          <td className="p-2 border font-mono text-[11px]">{r.preCheckoutPadrao}</td>
                          <td className="p-2 border font-mono text-[11px]">{r.preCheckoutGg}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="tags" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                4. Tags enviadas ao RD Station Marketing
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 border">Fluxo</th>
                        <th className="p-2 border">Tag específica</th>
                        <th className="p-2 border">Tag geral</th>
                        <th className="p-2 border">Tag de qualificação</th>
                        <th className="p-2 border">Parcial</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CAPTURE_FLOWS.map((flow) => {
                        const t = tagsForFlow(flow);
                        return (
                          <tr key={flow.source}>
                            <td className="p-2 border">{flow.label}</td>
                            <td className="p-2 border font-mono">{t.especifica}</td>
                            <td className="p-2 border font-mono">{t.geral}</td>
                            <td className="p-2 border font-mono">{t.qualificacao}</td>
                            <td className="p-2 border font-mono">{t.parcial}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Lógica centralizada em <code>src/lib/leads/taxonomy.ts</code> (<code>buildLeadTags</code>).
                  Descartados recebem <b>+ tag <code>EXCLUIR</code></b> automaticamente.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="crm" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                5. Roteamento no RD Station CRM
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 border">Fluxo</th>
                        <th className="p-2 border">Funil de destino</th>
                        <th className="p-2 border">Regra</th>
                        <th className="p-2 border">Anotação?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {CAPTURE_FLOWS.map((flow) => {
                        const routing = CRM_ROUTING_LABELS[flow.crm];
                        return (
                          <tr key={flow.source} className="align-top">
                            <td className="p-2 border">{flow.label}</td>
                            <td className="p-2 border font-semibold">
                              {routing.funil.map((line) => (
                                <div key={line}>{line}</div>
                              ))}
                            </td>
                            <td className="p-2 border text-muted-foreground">
                              {routing.regra.map((line) => (
                                <div key={line}>{line}</div>
                              ))}
                            </td>
                            <td className="p-2 border text-center">{flow.crm === "nao-envia" ? "—" : "✅"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 space-y-2 text-[11px] text-muted-foreground">
                  <p>
                    <b>Round-robin balanceado por bucket:</b> cada combinação{" "}
                    <code>funil × temperatura</code> tem seu próprio contador para distribuir igualitariamente
                    entre Bruna · Weruska · Ingrid. Vendedora fica salva em <code>crm_seller_email</code>.
                  </p>
                  <p>
                    <b>BLACK 2026 (fluxo próprio):</b> rodízio 1 a 1 entre Bruna, Daphne, Ingrid, Jana, Weruska e Patrícia.
                    RD CRM (BLACK 2 EM 1) para Bruna, Ingrid e Weruska; FEO CRM (BLACK 2026) para Daphne, Jana e Patrícia.
                  </p>
                  <p>
                    <b>Nome da negociação:</b>{" "}
                    <code>{"{FLUXO} - POS-{ÁREA} - {TEMPERATURA}"}</code> — ex.:{" "}
                    <code>AULAS-EXP - POS-ORTO - QUENTE</code>, <code>EMENTA - POS-DOR - MORNO</code>,{" "}
                    <code>PRE-CHK - POS-ESPORTE - QUENTE</code>.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="tracking" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                6. Rastreamento para tráfego pago (Meta / Google via GTM)
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs mb-2">
                  <div className="font-semibold text-admin-accent mb-1">
                    Toda a instrumentação Meta Pixel + CAPI está no GTM
                  </div>
                  <p className="text-muted-foreground">
                    O site <b>não dispara mais Meta Pixel nem chama a Conversions API</b>. Detalhes em{" "}
                    <Link to="/admin/plano-tracking-gtm" className="underline font-medium">
                      /admin/plano-tracking-gtm
                    </Link>.
                  </p>
                </div>
                <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">
                  <li>
                    Após salvar o lead, o cliente dispara no <code>window.dataLayer</code> os eventos{" "}
                    <code>feo_lead_saved</code> (parcial) e <code>feo_lead_qualified</code> (final).
                  </li>
                  <li>
                    O GTM escuta esses eventos e faz Meta Pixel + CAPI com{" "}
                    <code>event_id</code>/<code>_fbc</code>/<code>_fbp</code> gerenciados internamente.
                  </li>
                  <li>
                    RD Station Marketing recebe o lead via <code>rdstation.server.ts</code>, sem campos{" "}
                    <code>cf_meta_*</code>.
                  </li>
                  <li>
                    Secrets em uso: <code>RDSTATION_API_KEY</code>, <code>RDCRM_API_TOKEN</code>,{" "}
                    <code>RDCRM_DEAL_STAGE_ID</code>.
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="funil-etapas" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                7. Definição das etapas do funil (visão de conversão)
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 border">Etapa</th>
                        <th className="p-2 border">Fonte</th>
                        <th className="p-2 border">O que conta</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-2 border font-semibold">Sessões</td>
                        <td className="p-2 border font-mono text-[11px]">page_views</td>
                        <td className="p-2 border"><code>session_id</code> distintos com <code>path</code> em rotas de captura.</td>
                      </tr>
                      <tr>
                        <td className="p-2 border font-semibold">Iniciaram captura</td>
                        <td className="p-2 border font-mono text-[11px]">lead_capture_attempts</td>
                        <td className="p-2 border">Qualquer POST no endpoint (inclui validação e falhas).</td>
                      </tr>
                      <tr>
                        <td className="p-2 border font-semibold">Leads capturados</td>
                        <td className="p-2 border font-mono text-[11px]">leads</td>
                        <td className="p-2 border">Registros com status ok gravados no banco (parciais + finais).</td>
                      </tr>
                      <tr>
                        <td className="p-2 border font-semibold">Leads quentes</td>
                        <td className="p-2 border font-mono text-[11px]">leads</td>
                        <td className="p-2 border"><code>classificacao = 'quente'</code>.</td>
                      </tr>
                      <tr>
                        <td className="p-2 border font-semibold">Conversões</td>
                        <td className="p-2 border font-mono text-[11px]">sales (Eduzz)</td>
                        <td className="p-2 border">Compras pagas cruzadas por e-mail. CRM (WhatsApp) × MKT (LP / ads).</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="colunas" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                8. Colunas da tabela de leads
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1.5">
                  <li><b>Data</b> — data/hora da captura no fuso America/Sao_Paulo.</li>
                  <li>
                    <b>Lead</b> — nome, e-mail, telefone + badge de temperatura (❄️ Frio / 🌤️ Morno / 🔥 Quente).
                    Pré-checkout é sempre Quente. AprovaFEO não tem temperatura.
                  </li>
                  <li>
                    <b>Origem</b> — etiqueta amigável (<code>Aulas Exp ORTO</code>, <code>Ementa DOR</code>,{" "}
                    <code>Pré-chk GERONTO</code>) + link direto para a LP.
                  </li>
                  <li>
                    <b>RD Station</b> — status de envio + tags reais enviadas (união de todas as capturas do
                    mesmo e-mail).
                  </li>
                  <li>
                    <b>RD CRM</b> — status, funil (UNIVERSITÁRIOS / FUNIL-POS) e vendedora responsável
                    (👩‍💼 Bruna / Weruska / Ingrid).
                  </li>
                  <li>
                    <b>Compras</b> — cruzamento por e-mail contra <code>public.sales</code> (Eduzz, só pagas).
                    Formato <code>PRODUTO - FONTE</code>.{" "}
                    <b className="text-purple-400">CRM</b> = WhatsApp (<code>utm_source=CRM</code>).{" "}
                    <b className="text-emerald-400">MKT</b> = site/LP/ads.
                  </li>
                  <li><b>Ações</b> — WhatsApp direto e exclusão.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="fluxo" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                9. Passo a passo — o que acontece com cada lead
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <ol className="list-decimal pl-5 space-y-1.5 text-xs text-muted-foreground">
                  <li>
                    <b>Cliente</b> · usuário preenche o formulário. Validação Zod (nome ≥ 2, e-mail válido,
                    telefone com DDD 10–13 dígitos) roda antes do submit.
                  </li>
                  <li>
                    <b>Cliente</b> · <code>client-tracking.ts</code> lê UTMs. Cookies do Meta são
                    responsabilidade do GTM.
                  </li>
                  <li>
                    <b>Servidor</b> · POST chega ao server function, que delega para{" "}
                    <code>postprocess.server.ts</code>. Revalidação Zod.
                  </li>
                  <li>
                    <b>Servidor</b> · <code>qualifyByAnswers</code> classifica em FRIO/MORNO/QUENTE ou marca
                    DESCARTADO. <code>buildLeadTags</code> deriva tags. <code>resolveCrmRouting</code>{" "}
                    escolhe funil (UNIVERSITÁRIOS / FUNIL-POS / SKIP).
                  </li>
                  <li>
                    <b>Banco</b> · lead gravado em <code>public.leads</code> com{" "}
                    <code>delivered_to_rdstation</code> / <code>delivered_to_crm</code>.
                  </li>
                  <li>
                    <b>RD Marketing</b> · <code>POST /conversions</code> com identificador + tags.
                    Descartados recebem <code>+EXCLUIR</code>.
                  </li>
                  <li>
                    <b>RD CRM</b> · se elegível, cria deal no funil correto. Vendedora por round-robin
                    balanceado + anotação estruturada anexada.
                  </li>
                  <li>
                    <b>Cliente</b> · <code>pushPixelLead</code> emite <code>feo_lead_saved</code> /{" "}
                    <code>feo_lead_qualified</code> no dataLayer.
                  </li>
                  <li>
                    <b>Auditoria</b> · tentativa registrada em <code>lead_capture_attempts</code>.
                  </li>
                  <li>
                    <b>Painel</b> · linha aparece na tabela com todos os badges.
                  </li>
                </ol>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="dev" className="border rounded-lg border-border/60 bg-muted/10 px-4">
              <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
                10. Como adicionar uma nova captura (dev checklist)
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground">
                  <li>
                    Se for um novo <b>source</b>, adicionar em <code>LeadCaptureSource</code>{" "}
                    (<code>src/lib/leads/taxonomy.ts</code>) e cobrir em <code>buildLeadTags</code>.
                  </li>
                  <li>
                    Criar server function em <code>src/lib/&lt;area&gt;/lead.functions.ts</code> seguindo o
                    padrão (Zod → RD Marketing → RD CRM opcional → insert em <code>leads</code> →{" "}
                    <code>runPostCapture</code>).
                  </li>
                  <li>
                    Adicionar entrada em <code>CAPTURE_FLOWS</code> de{" "}
                    <code>src/lib/leads/capture-flow-catalog.ts</code> — este painel se atualiza sozinho.
                  </li>
                  <li>Se for uma nova pós, adicionar entrada em <code>POS_LPS</code> no mesmo arquivo.</li>
                  <li>Publicar. Verificar no Log de captura que o lead entrou no fluxo correto.</li>
                </ol>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Estado atual */}
          <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
            <div className="font-semibold text-emerald-400 mb-1 flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Estado atual
            </div>
            <p className="text-muted-foreground">
              Pipeline único server-side, validação rígida em todas as fontes, builder de deal resiliente,
              round-robin balanceado por bucket, evento no <code>dataLayer</code> para GTM/Meta,
              log de captura em tempo real e cruzamento automático com vendas Eduzz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
